/**
 * DaVinci PerformAction Endpoint
 *
 * Handles all DaVinci action types:
 * - BA (Booking Action) - Availability check
 * - B (Fixed Booking) - Direct booking for code 712
 * - BQ (Booking Query) - Request booking for code 733
 * - BC (Booking Confirmation) - Confirmation for code 923
 */

import { davinciClient } from './davinciClient';
import {
  PerformActionRequestSchema,
  PerformActionResponseSchema,
  type PerformActionRequest,
  type PerformActionResponse,
  type DaVinciLogin,
} from '@/types/schemas';

/**
 * Generic PerformAction function
 *
 * @param request - Complete PerformAction request
 * @returns PerformAction response with SessionId, ActivityId, Booking
 *          Note: ReturnCode is inside response.Booking.ReturnCode
 * @throws {DaVinciNetworkError} Network or timeout errors
 * @throws {DaVinciApiError} API errors
 * @throws {ValidationError} Invalid request/response structure
 *
 * @example
 * const response = await performAction({
 *   Login: {},
 *   Booking: { ActionCode: 'BA', ... }
 * });
 * // Access return code: response.Booking.ReturnCode
 */
export async function performAction(request: PerformActionRequest): Promise<PerformActionResponse> {
  const response = await davinciClient.post<PerformActionRequest, PerformActionResponse>({
    endpoint: '/PerformAction',
    data: request,
    requestSchema: PerformActionRequestSchema,
    responseSchema: PerformActionResponseSchema,
  });

  return response;
}

/**
 * Convenience wrapper for BA (Booking Action)
 * Use to check availability
 *
 * @param booking - Booking object with BA action
 * @param login - Empty login object (auth handled by API Gateway)
 * @returns Response with availability status
 *          Note: ReturnCode is inside response.Booking.ReturnCode (712/733/923)
 *
 * @example
 * const response = await performBookingAction(
 *   {
 *     ActionCode: 'BA',
 *     AgencyNumber: 1,
 *     Touroperator: 'VAMOS',
 *     TravelType: 'VR26',
 *     // ... rest of booking data
 *   },
 *   {}
 * );
 * // Access return code: response.Booking.ReturnCode
 */
export async function performBookingAction(
  booking: Record<string, unknown>,
  login: DaVinciLogin
): Promise<PerformActionResponse> {
  return performAction({
    Login: login,
    Booking: booking,
  });
}

/**
 * Convenience wrapper for B (Fixed Booking)
 * Use after BA returns 712
 *
 * @param sessionId - SessionId from BA response
 * @param activityId - ActivityId from ConvertResponse
 * @param booking - Transformed booking from ConvertResponse
 * @param login - Empty login object (auth handled by API Gateway)
 * @param delivery - Delivery configuration (optional)
 * @param payment - Payment configuration (optional)
 * @returns Response with booking confirmation (ReturnCode 200 = success)
 *
 * @example
 * const response = await performFixedBooking(
 *   sessionId,
 *   activityId,
 *   convertedBooking,
 *   {},
 *   deliveryConfig,
 *   paymentConfig
 * );
 */
export async function performFixedBooking(
  sessionId: string,
  activityId: string,
  booking: Record<string, unknown>,
  login: DaVinciLogin,
  delivery?: Record<string, unknown>,
  payment?: Record<string, unknown>
): Promise<PerformActionResponse> {
  return performAction({
    Login: login,
    SessionId: sessionId,
    ActivityId: activityId,
    Booking: { ...booking, ActionCode: 'B' },
    Delivery: delivery,
    Payment: payment,
  });
}

/**
 * Convenience wrapper for BQ (Booking Query - Request Booking)
 * Use after BA returns 733
 *
 * @param sessionId - SessionId from BA response
 * @param activityId - ActivityId from ConvertResponse
 * @param booking - Transformed booking from ConvertResponse (with Client info)
 * @param login - Empty login object (auth handled by API Gateway)
 * @param delivery - Delivery configuration (optional)
 * @param payment - Payment configuration (optional)
 * @returns Response with request status (ReturnCode 206 = success)
 *
 * @example
 * const response = await performBookingQuery(
 *   sessionId,
 *   activityId,
 *   { ...convertedBooking, Client: { Name: 'John', ... } },
 *   {},
 *   deliveryConfig,
 *   paymentConfig
 * );
 */
export async function performBookingQuery(
  sessionId: string,
  activityId: string,
  booking: Record<string, unknown>,
  login: DaVinciLogin,
  delivery?: Record<string, unknown>,
  payment?: Record<string, unknown>
): Promise<PerformActionResponse> {
  return performAction({
    Login: login,
    SessionId: sessionId,
    ActivityId: activityId,
    Booking: { ...booking, ActionCode: 'BQ' },
    Delivery: delivery,
    Payment: payment,
  });
}

/**
 * Convenience wrapper for BC (Booking Confirmation)
 * Use after BA returns 923
 *
 * @param sessionId - SessionId from BA response
 * @param activityId - ActivityId from ConvertResponse
 * @param booking - Transformed booking from ConvertResponse
 * @param login - Empty login object (auth handled by API Gateway)
 * @param delivery - Delivery configuration (optional)
 * @param payment - Payment configuration (optional)
 * @returns Response with booking confirmation (ReturnCode 200 = success)
 *
 * @example
 * const response = await performBookingConfirmation(
 *   sessionId,
 *   activityId,
 *   convertedBooking,
 *   {},
 *   deliveryConfig,
 *   paymentConfig
 * );
 */
export async function performBookingConfirmation(
  sessionId: string,
  activityId: string,
  booking: Record<string, unknown>,
  login: DaVinciLogin,
  delivery?: Record<string, unknown>,
  payment?: Record<string, unknown>
): Promise<PerformActionResponse> {
  return performAction({
    Login: login,
    SessionId: sessionId,
    ActivityId: activityId,
    Booking: { ...booking, ActionCode: 'BC' },
    Delivery: delivery,
    Payment: payment,
  });
}
